import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import AppsIcon from '@material-ui/icons/Apps';
import CodeIcon from '@material-ui/icons/Code';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import GiftCardIcon from '@material-ui/icons/CardGiftcard';
import GroupIcon from '@material-ui/icons/Group';
import HomeIcon from '@material-ui/icons/Home';
import LayersIcon from '@material-ui/icons/Layers';
import List from '@material-ui/core/List';
import ListIcon from '@material-ui/icons/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import MenuIcon from '@material-ui/icons/Menu';
import PagesIcon from '@material-ui/icons/InsertDriveFile';
import PeopleIcon from '@material-ui/icons/People';
import SettingsIcon from '@material-ui/icons/Settings';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import styled from 'styled-components';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import WidgetsIcon from '@material-ui/icons/Widgets';
import { useAuth0 } from '@auth0/auth0-react';
import startCase from 'lodash/startCase';
import Link from '@/components/Link';

import { useStores } from '../hooks/useStores';

import Avatar from './Avatar';
import PercentIcon from './PercentIcon';
import {
  fetchFlags,
  LD_FLAG_NEW_FEATURE,
  LD_FLAG_USERS_AND_PERMISSIONS,
  LD_FLAG_BLOCKS,
  LD_FLAG_MENU_EDITOR,
  LD_FLAG_SITE_TAGGING,
  LD_FLAG_ELEMENT_MYVOLUSION_LAUNCH_DATE,
} from '@/utils/fetchLaunchDarklyFlags';
import {
  NEW_FEATURE_FLAG_STORE_CREDIT,
  NEW_FEATURE_FLAG_DISCOUNTS,
} from '@/constants/newFeatureFlags';
import { nameFromUser } from '@/utils/user';

const NavContainer = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
`;

const NavItem = styled.div`
  text-decoration: none;
  text-transform: capitalize;
`;

const StyledIcon = styled(ListItemIcon)`
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme, selected }) =>
    selected ? theme.colors.actions.primary.main : theme.colors.neutrals[8]};
  margin-right: 0;
  min-width: auto !important;
  ${(props) => {
    return props.$verticalRule
      ? `
      ::before {
        content: "";
        background-color: rgba(0, 0, 0, 0.32);
        height: ${props.$verticalRule * 25}px;
        z-index: -1;
        position: absolute;
        width: 2px;
        top: -${(props.$verticalRule - 1) * 25 + 10}px;
      }`
      : '';
  }}

  svg {
    color: white;
    font-size: 16px;
  }
`;

const StyledLogo = styled.img`
  width: 134px;
  margin: 12px 20px 16px;
  height: 26px;
`;

const StyledList = styled(List)`
  width: 210px;
  margin-bottom: 40px;
`;

const StyledFooter = styled(List)`
  && {
    width: 210px;
    position: fixed;
    bottom: 0;
    padding: 0;
    background-color: ${({ theme }) => theme.colors.whites[9]};
  }
`;

const StyledName = styled.span`
  margin-left: 16px;
  color: ${({ theme }) => theme.colors.text[1]};
`;

const StyledListItemText = styled(ListItemText)`
  && {
    span {
      font-size: 14px !important;
      color: ${({ theme }) => theme.colors.text[1]};
      margin-left: 0.75rem;
    }
  }
`;

const NavigationItem = ({
  to,
  label,
  icon,
  selected,
  verticalRule,
  redirectPath,
  redirect,
}) => {
  const testId = label.replace(/\s/g, '');
  return (
    <NavItem data-testid={testId}>
      <Link href={to} redirectPath={redirectPath} redirect={redirect}>
        <ListItem selected={selected} button>
          <StyledIcon
            data-testid={`${testId}-icon`}
            label={label}
            $verticalRule={verticalRule}
            selected={selected}
          >
            {icon}
          </StyledIcon>
          <StyledListItemText primary={label} />
        </ListItem>
      </Link>
    </NavItem>
  );
};

const Navigation = ({ variant, open, onClose, currentRoute }) => {
  const ldClient = useLDClient();
  const [newFeaturesEnabled, setNewFeaturesEnabled] = useState({});
  const [otherFlags, setOtherFlags] = useState({});
  const { user } = useAuth0();
  const { storeInformation } = useStores();

  const name = nameFromUser(user);

  const storeCreatedAfterElementLaunched = () => {
    const launchDate = otherFlags[LD_FLAG_ELEMENT_MYVOLUSION_LAUNCH_DATE];
    if (!launchDate) {
      return true;
    }

    const testPattern = /\d{4}, ?\d{2}, ?\d{2}/;
    if (!testPattern.test(launchDate)) {
      return true;
    }

    const [year, month, day] = launchDate.split(',').map((e) => e.trim());
    const elementLaunchDate = new Date(year, month - 1, day);
    const storeCreatedOn = new Date(storeInformation.info.createdOn);

    return storeCreatedOn.getTime() >= elementLaunchDate.getTime();
  };

  useEffect(() => {
    const fetchFlagsAsync = async () => {
      const result = await fetchFlags(ldClient, user);

      if (result !== undefined) {
        if (result[LD_FLAG_NEW_FEATURE] !== undefined) {
          const newFeatures = {};
          result[LD_FLAG_NEW_FEATURE].forEach((f) => {
            newFeatures[f] = true;
          });
          setNewFeaturesEnabled(newFeatures);
        }

        const flags = {};
        [
          LD_FLAG_USERS_AND_PERMISSIONS,
          LD_FLAG_BLOCKS,
          LD_FLAG_MENU_EDITOR,
          LD_FLAG_SITE_TAGGING,
          LD_FLAG_ELEMENT_MYVOLUSION_LAUNCH_DATE,
        ].forEach((flag) => {
          if (result[flag] !== undefined) {
            flags[flag] = result[flag];
          }
        });
        setOtherFlags(flags);
      }
    };
    fetchFlagsAsync();
  }, [ldClient, user]);

  const navItems = [
    {
      label: 'home',
      to: '/admin',
      redirectPath: '/dashboard',
      redirect: !newFeaturesEnabled.dashboard,
      icon: <HomeIcon />,
      notifications: 0,
    },
    {
      ldKey: 'orders',
      label: 'orders',
      to: '/admin/orders/all',
      redirectPath: '/order/list',
      redirect: !newFeaturesEnabled.orders,
      icon: <ShoppingCartIcon />,
      notifications: 0,
    },
    {
      label: 'products',
      to: '/admin/products/all',
      redirectPath: '/product/list',
      redirect: !newFeaturesEnabled['products.list'],
      icon: <LocalOfferIcon />,
      notifications: 0,
    },
    {
      label: 'discounts',
      to: '/admin/discounts/all',
      icon: <PercentIcon />,
      hidden: !newFeaturesEnabled[NEW_FEATURE_FLAG_DISCOUNTS],
    },
    {
      label: 'gift cards',
      to: '/admin/storeCredit/all',
      redirectPath: '/',
      redirect: !newFeaturesEnabled.storeCredit,
      icon: <GiftCardIcon />,
      notifications: 0,
      hidden: !newFeaturesEnabled[NEW_FEATURE_FLAG_STORE_CREDIT],
    },
    {
      label: 'categories',
      to: '/admin/categories',
      redirectPath: '/categories/list',
      redirect: !newFeaturesEnabled.categories,
      icon: <ListIcon />,
      notifications: 0,
    },
    {
      label: 'customers',
      to: '/admin/customers',
      redirectPath: '/customer/list',
      redirect: !newFeaturesEnabled.customers,
      icon: <PeopleIcon />,
      notifications: 0,
    },
    {
      label: 'integrations',
      to: '/apps',
      redirectPath: '/appstore2',
      redirect: !newFeaturesEnabled.apps,
      icon: <AppsIcon />,
      notifications: 0,
    },
    {
      label: 'settings',
      to: '/settings',
      redirectPath: '/settings/detail',
      redirect: !newFeaturesEnabled.settings,
      icon: <SettingsIcon />,
      notifications: 0,
    },
    {
      label: 'analytics',
      to: '/analytics',
      redirectPath: '/analytics',
      redirect: !newFeaturesEnabled.analytics,
      icon: <TrendingUpIcon />,
      notifications: 0,
    },
    {
      label: 'users & permissions',
      to: '/users',
      redirectPath: '/designer/users',
      redirect: !newFeaturesEnabled.siteDesigner,
      icon: <GroupIcon />,
      notifications: 0,
      hidden: !otherFlags[LD_FLAG_USERS_AND_PERMISSIONS],
    },
    {
      label: 'blocks',
      to: '/designer/blocks',
      redirectPath: '/designer/blocks',
      redirect: !newFeaturesEnabled.blocks,
      icon: <WidgetsIcon />,
      notifications: 0,
      hidden: !otherFlags[LD_FLAG_BLOCKS],
    },
    {
      label: 'site designer',
      to: '/designer',
      icon: <LayersIcon />,
      notifications: 0,
    },
    {
      label: 'site tagging',
      to: '/sitetagging',
      redirectPath: '/site-tagging',
      redirect: !otherFlags[LD_FLAG_SITE_TAGGING],
      hidden: !otherFlags[LD_FLAG_SITE_TAGGING],
      icon: <CodeIcon />,
      notifications: 0,
    },
    {
      label: 'pages',
      to: '/pages/list',
      hidden: storeCreatedAfterElementLaunched(),
      icon: <PagesIcon />,
      notifications: 0,
    },
    {
      label: 'menu editor',
      to: '/editstore/menu',
      redirectPath: otherFlags[LD_FLAG_MENU_EDITOR]
        ? '/designer/menus'
        : '/editstore/menu',
      redirect: true,
      icon: <MenuIcon />,
      notifications: 0,
      verticalRule: storeCreatedAfterElementLaunched() ? 3 : 5,
    },
  ];

  return (
    <NavContainer>
      <Drawer elevation={0} open={open} onClose={onClose} variant={variant}>
        <div tabIndex={0} role="button" onClick={onClose} onKeyDown={onClose}>
          <StyledList>
            <StyledLogo
              className="logo"
              src="https://d3ggqrmoaaez04.cloudfront.net/images/logos/logo.svg"
              alt="Volusion Logo"
            />
            {navItems.map(
              (item) =>
                !item.hidden && (
                  <NavigationItem
                    key={item.label}
                    backgroundColor={item.backgroundColor}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    verticalRule={item.verticalRule || false}
                    selected={currentRoute === item.label}
                    redirectPath={item.redirectPath}
                    redirect={item.redirect}
                  />
                )
            )}
          </StyledList>
          <StyledFooter>
            <Divider />
            <Link
              href="/my-account"
              redirectPath="/myaccount"
              redirect={!newFeaturesEnabled.myAccount}
            >
              <ListItem button>
                <Avatar
                  firstName={name.firstName}
                  lastName={name.lastName}
                  url=""
                />
                <StyledName data-testid="user-fullname">
                  {startCase(`${name.firstName} ${name.lastName}`)}
                </StyledName>
              </ListItem>
            </Link>
          </StyledFooter>
        </div>
      </Drawer>
    </NavContainer>
  );
};

export default withLDProvider({
  clientSideID: process.env.NEXT_PUBLIC_LAUNCH_DARKLY_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(observer(Navigation));
